import { createContext, PropsWithChildren, useCallback, useContext, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

export const designerScreenAttributeNames = [
	'ScreenArea', 'ScreenName', 'ScreenSelectionType', 'ScreenSelectionName', 'ScreenReadOnly',
	'AppDefinitionId', 'AppVersionId', 'ViewId', 'SharedComponentId', 'ResourceId', 'ThemeId', 'ThemeName',
	'ComponentId', 'ComponentPath', 'ReactComponentId', 'ReactComponentName', 'ReactComponentHostState', 'ReactSourceFileId', 'ReactSourceFilePath',
	'ContextId', 'OperationId', 'EntityId', 'EnumId', 'EventHandlerId', 'IntegrationEventId', 'CodeFileId', 'CodeFilePath',
	'FlowDefinitionId', 'CurrentActivityId', 'CurrentTransitionId', 'FlowInstanceId', 'CurrentTemplateId',
] as const;
export type DesignerScreenAttributes = Partial<Record<typeof designerScreenAttributeNames[number], string | undefined>>;
type Entry = { route: string; priority: number; attributes: Record<string, string> };
type Registry = {
	route: string;
	register: (owner: symbol, entry: Entry) => void;
	unregister: (owner: symbol) => void;
	getAttributes: () => Record<string, string>;
	subscribe: (listener: () => void) => () => void;
};
const emptyAttributes = () => ({});
const Context = createContext<Registry>({ route: '', register: () => undefined, unregister: () => undefined, getAttributes: emptyAttributes, subscribe: () => () => undefined });

export function DesignerScreenContextProvider({ children, currentChangeSetId }: PropsWithChildren<{ currentChangeSetId?: string | null }>) {
	const location = useLocation();
	const entries = useRef(new Map<symbol, Entry>());
	const listeners = useRef(new Set<() => void>());
	const notify = useCallback(() => { listeners.current.forEach(listener => listener()); }, []);
	const subscribe = useCallback((listener: () => void) => {
		listeners.current.add(listener);
		return () => { listeners.current.delete(listener); };
	}, []);
	const route = location.key;
	const routeRef = useRef(route);
	const changeSetRef = useRef(currentChangeSetId);
	useLayoutEffect(() => { changeSetRef.current = currentChangeSetId; routeRef.current = route; notify(); }, [currentChangeSetId, route, notify]);
	const register = useCallback((owner: symbol, entry: Entry) => { entries.current.set(owner, entry); notify(); }, [notify]);
	const unregister = useCallback((owner: symbol) => { entries.current.delete(owner); notify(); }, [notify]);
	const getAttributes = useCallback(() => {
		// Return one complete registration, never merge stale selection keys across artifacts.
		const active = Array.from(entries.current.values()).filter(entry => entry.route === routeRef.current)
			.sort((a, b) => b.priority - a.priority)[0];
		return { ...active?.attributes, ...(changeSetRef.current ? { ChangeSetId: changeSetRef.current } : {}) };
	}, []);
	const value = useMemo(() => ({ route, register, unregister, getAttributes, subscribe }), [route, register, unregister, getAttributes, subscribe]);
	return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Publish only compact artifact identifiers/names. No source, credentials, authority or URLs. */
export function useDesignerScreenContext(attributes: DesignerScreenAttributes, priority = 0, enabled = true) {
	const { route, register, unregister } = useContext(Context);
	const owner = useRef(Symbol('designer-screen'));
	const snapshot = JSON.stringify(Object.fromEntries(designerScreenAttributeNames.flatMap(key => {
		const value = attributes[key]?.trim();
		return value ? [[key, value]] : [];
	})));
	useLayoutEffect(() => {
		if (!enabled) return;
		const token = owner.current;
		register(token, { route, priority, attributes: JSON.parse(snapshot) });
		return () => unregister(token);
	}, [snapshot, priority, enabled, route, register, unregister]);
}

/** Stable getter: read at submission without rebuilding the Architect adapter/session on navigation. */
export const useDesignerScreenAttributes = () => useContext(Context).getAttributes;

/** Subscribe only the preview, not the Architect adapter or chat session, to selection changes. */
export function useDesignerScreenPreview() {
	const { getAttributes, subscribe } = useContext(Context);
	const getSnapshot = useCallback(() => JSON.stringify(getAttributes()), [getAttributes]);
	const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	return useMemo<Record<string, string>>(() => JSON.parse(snapshot), [snapshot]);
}
